import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import { usePublisherDetail } from '../../../hooks/usePublisherDetail';
import { useMarketplacePermissions } from '../../../hooks/useMarketplacePermissions';
import { usePublisherManage } from '../../../hooks/usePublisherManage';
import { usePublisherActivity } from '../../../hooks/usePublisherActivity';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { PublisherSocials } from 'types/publisher';
import type { PublisherChannel } from '../../../api/marketplaceApi';
import {
  uploadPublisherLogo,
  uploadPublisherBanner,
  deletePublisherLogo,
  deletePublisherBanner,
} from '../../../api/marketplaceApi';
import LocaleTabsMolecule from '../../molecules/marketplace/LocaleTabsMolecule';
import LocaleManagerMolecule from '../../molecules/marketplace/LocaleManagerMolecule';
import StickyUnsavedBarMolecule from '../../molecules/marketplace/StickyUnsavedBarMolecule';
import PublisherStatusCalloutMolecule from '../../molecules/marketplace/PublisherStatusCalloutMolecule';
import MarketplaceActivityRowMolecule from '../../molecules/marketplace/MarketplaceActivityRowMolecule';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import IconPickerAtom from '../../atoms/IconPicker/IconPickerAtom';
import SocialLinksMolecule from '../../molecules/marketplace/SocialLinksMolecule';
import UploadAttachmentMolecule from '../../molecules/UploadAttachment/UploadAttachmentMolecule';
import PublisherTeamSectionOrganism from './PublisherTeamSectionOrganism';
import './PublisherManageOrganism.scss';

type ManageSection = 'profile' | 'branding' | 'channels' | 'team' | 'verification' | 'dangerZone';

interface ManageDraft {
  displayName_i18n: Record<string, string>;
  bio_i18n: Record<string, string>;
  locales: string[];
  defaultLocale: string;
  website: string;
  socials: Partial<PublisherSocials>;
  hue: number;
  glyph: string;
  glyphKind: 'letter' | 'icon';
}

const NAV_SECTIONS: ReadonlyArray<{ id: ManageSection; icon: string; danger?: boolean }> = [
  { id: 'profile', icon: 'pi-user' },
  { id: 'branding', icon: 'pi-palette' },
  { id: 'channels', icon: 'pi-box' },
  { id: 'team', icon: 'pi-users' },
  { id: 'verification', icon: 'pi-shield' },
  { id: 'dangerZone', icon: 'pi-exclamation-triangle', danger: true },
];

// Design-specified preset hues (v3-publisher-manage.jsx:579-589)
const HUE_PRESETS = [210, 160, 270, 320, 340, 30, 40, 195, 0];

const STATUS_PILL_KEY: Record<string, string> = {
  draft: 'marketplace.publishers.statusPill.draft',
  pending_review: 'marketplace.publishers.statusPill.pendingReview',
  published: 'marketplace.publishers.statusPill.published',
  rejected: 'marketplace.publishers.statusPill.rejected',
};

const STATUS_PILL_ICON: Record<string, string> = {
  draft: 'pi-file-edit',
  pending_review: 'pi-clock',
  published: 'pi-check-circle',
  rejected: 'pi-times-circle',
};

const CHANNEL_STATUS_KEY: Record<string, string> = {
  approved: 'marketplace.publishers.manage.channels.statusLive',
  pending_review: 'marketplace.publishers.manage.channels.statusInReview',
  rejected: 'marketplace.publishers.manage.channels.statusRejected',
};

const CHANNEL_STATUS_CLASS: Record<string, string> = {
  approved: 'marketplace-publisher-manage__channel-status--approved',
  pending_review: 'marketplace-publisher-manage__channel-status--pending',
  rejected: 'marketplace-publisher-manage__channel-status--rejected',
};

function makeDraft(publisher: ReturnType<typeof usePublisherDetail>['publisher']): ManageDraft {
  if (!publisher) throw new Error('publisher required');
  const defaultLocale = publisher.default_locale || 'en-US';
  return {
    displayName_i18n: publisher.display_name_i18n && Object.keys(publisher.display_name_i18n).length > 0
      ? { ...publisher.display_name_i18n }
      : { [defaultLocale]: publisher.display_name },
    bio_i18n: publisher.bio_i18n && Object.keys(publisher.bio_i18n).length > 0
      ? { ...publisher.bio_i18n }
      : { [defaultLocale]: publisher.bio ?? '' },
    locales: publisher.locales?.length ? [...publisher.locales] : [defaultLocale],
    defaultLocale,
    website: publisher.website ?? '',
    socials: publisher.socials ? { ...publisher.socials } : {},
    hue: publisher.hue ?? 210,
    glyph: publisher.glyph ?? publisher.display_name.charAt(0).toUpperCase(),
    glyphKind: publisher.glyph_kind ?? 'letter',
  };
}

function resolveChannelName(name: PublisherChannel['name']): string {
  if (typeof name === 'string') return name;
  const locName = name as unknown as Record<string, string> | null | undefined;
  return locName?.['default'] ?? locName?.['en-US'] ?? '';
}

// Shared section card used throughout manage sections
function ManageSectionCard({
  title,
  subtitle,
  action,
  danger = false,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={[
        'marketplace-publisher-manage__section-card',
        danger ? 'marketplace-publisher-manage__section-card--danger' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="marketplace-publisher-manage__section-card-header">
        <div className="marketplace-publisher-manage__section-card-titles">
          <h2
            className={[
              'marketplace-publisher-manage__section-card-title',
              danger ? 'marketplace-publisher-manage__section-card-title--danger' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="marketplace-publisher-manage__section-card-subtitle">{subtitle}</p>
          )}
        </div>
        {action && (
          <div className="marketplace-publisher-manage__section-card-action">{action}</div>
        )}
      </header>
      <div className="marketplace-publisher-manage__section-card-body">{children}</div>
    </section>
  );
}

function SocialInputRow({
  icon,
  prefix,
  label,
  value,
  onChange,
  placeholder,
}: {
  icon: string;
  prefix: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="marketplace-publisher-manage__field">
      <label className="marketplace-publisher-manage__label">{label}</label>
      <div className="marketplace-publisher-manage__social-input-row">
        <span className="marketplace-publisher-manage__social-prefix">
          <i className={`pi ${icon}`} aria-hidden="true" />
          <span>{prefix}</span>
        </span>
        <input
          type="text"
          className="marketplace-publisher-manage__input marketplace-publisher-manage__input--social"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}

function ManagedChannelRow({
  channel,
  publisherHue,
  onOpen,
}: {
  channel: PublisherChannel;
  publisherHue: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const name = resolveChannelName(channel.name);
  const statusClass = CHANNEL_STATUS_CLASS[channel.status] ?? '';
  const statusKey = CHANNEL_STATUS_KEY[channel.status] ?? channel.status;

  return (
    <div className="marketplace-publisher-manage__channel-row-v2">
      <div
        className="marketplace-publisher-manage__channel-row-glyph"
        style={{
          background: `linear-gradient(155deg, hsl(${publisherHue} 70% 55%), hsl(${(publisherHue + 25) % 360} 60% 35%))`,
        }}
        aria-hidden="true"
      >
        {channel.glyph && channel.glyph_kind === 'icon' ? (
          // `glyph` is a PrimeIcon class name (e.g. "pi-compass") — render as
          // an icon rather than a literal text string (fixes garbled glyph bug).
          <i className={`pi ${channel.glyph}`} />
        ) : (
          <span>{(channel.glyph || name.charAt(0)).toUpperCase()}</span>
        )}
      </div>

      <div className="marketplace-publisher-manage__channel-row-info">
        <span className="marketplace-publisher-manage__channel-row-name">{name}</span>
        <span className="marketplace-publisher-manage__channel-row-meta">
          {channel.subscriber_count != null && (
            <>
              {channel.subscriber_count.toLocaleString()}{' '}
              {t('marketplace.publishers.manage.channels.subsLabel')}
            </>
          )}
          {channel.subscriber_count != null && channel.item_count != null && ' · '}
          {channel.item_count != null && (
            <>
              {channel.item_count} {t('marketplace.publishers.manage.channels.itemsLabel')}
            </>
          )}
        </span>
      </div>

      <span className={`marketplace-publisher-manage__channel-status-pill ${statusClass}`}>
        {t(statusKey)}
      </span>

      <button
        type="button"
        className="marketplace-publisher-manage__channel-row-action"
        disabled
        title={t('marketplace.publishers.manage.channels.manageComingSoon')}
        aria-label={t('marketplace.publishers.manage.channels.manageComingSoon')}
      >
        <i className="pi pi-cog" aria-hidden="true" />
      </button>

      <button
        type="button"
        className="marketplace-publisher-manage__channel-row-action"
        disabled
        title={t('marketplace.publishers.manage.channels.transferComingSoon')}
        aria-label={t('marketplace.publishers.manage.channels.transferComingSoon')}
      >
        <i className="pi pi-arrow-right-arrow-left" aria-hidden="true" />
      </button>

      <button
        type="button"
        className="marketplace-publisher-manage__channel-row-open"
        onClick={onOpen}
      >
        {t('marketplace.publishers.manage.channels.openBtn')}
        <i className="pi pi-chevron-right" aria-hidden="true" />
      </button>
    </div>
  );
}

export interface PublisherManageOrganismProps {
  slug: string;
  onBack?: () => void;
  onChannelClick?: (channelId: string) => void;
  onCreateChannel?: () => void;
  className?: string;
}

const PublisherManageOrganism: React.FC<PublisherManageOrganismProps> = ({
  slug,
  onBack,
  onChannelClick,
  onCreateChannel,
  className = '',
}) => {
  const { t } = useTranslation();

  const { publisher, channels, loading, error, refetch } = usePublisherDetail(slug);
  const {
    permissions,
    unavailable: permissionsUnavailable,
    loading: permissionsLoading,
    refetch: refetchPermissions,
  } = useMarketplacePermissions(slug);
  const manage = usePublisherManage();
  const { activity, loading: activityLoading } = usePublisherActivity(slug);
  const apiClients = useApiClientsOrNull();

  const [activeSection, setActiveSection] = useState<ManageSection>('profile');
  const [activeLocale, setActiveLocale] = useState<string>('en-US');
  const [draft, setDraft] = useState<ManageDraft | null>(null);
  // Controls whether the full dropzone is visible. Default false — the compact
  // "Upload logo" + "Use initial" button pair shows first; clicking "Upload logo"
  // expands the dropzone inline.
  const [showLogoDropzone, setShowLogoDropzone] = useState(false);
  // Same pattern for the banner section.
  const [showBannerDropzone, setShowBannerDropzone] = useState(false);
  // Track previous glyphKind when entering upload mode so we can restore it when exiting
  const previousGlyphKindRef = useRef<'letter' | 'icon'>('letter');
  const savedDraftJSON = useRef<string | null>(null);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (publisher && !draft) {
      const initial = makeDraft(publisher);
      setDraft(initial);
      savedDraftJSON.current = JSON.stringify(initial);
      setActiveLocale(publisher.default_locale || 'en-US');
    }
  }, [publisher, draft]);

  const isDirty =
    draft !== null &&
    savedDraftJSON.current !== null &&
    JSON.stringify(draft) !== savedDraftJSON.current;

  const handleSave = useCallback(async () => {
    if (!draft) return;
    setSaveError(null);
    try {
      await manage.save(slug, {
        displayName: draft.displayName_i18n[draft.defaultLocale] || publisher?.display_name || '',
        bio: draft.bio_i18n[draft.defaultLocale] || undefined,
        locales: draft.locales,
        defaultLocale: draft.defaultLocale,
        website: draft.website || null,
        socials: Object.keys(draft.socials).length > 0 ? draft.socials : null,
        hue: draft.hue,
        glyph: draft.glyph || undefined,
        glyphKind: draft.glyphKind,
      });
      savedDraftJSON.current = JSON.stringify(draft);
      refetch();
    } catch {
      setSaveError(t('marketplace.publishers.manage.saveError'));
    }
  }, [slug, draft, publisher, manage, refetch, t]);

  const handleDiscard = useCallback(() => {
    if (!publisher) return;
    const initial = makeDraft(publisher);
    setDraft(initial);
    savedDraftJSON.current = JSON.stringify(initial);
    setSaveError(null);
  }, [publisher]);

  const handleLocalesChange = useCallback(
    (newLocales: string[]) => {
      setDraft((d) => {
        if (!d) return d;
        const added = newLocales.filter((l) => !d.locales.includes(l));
        const displayName_i18n = { ...d.displayName_i18n };
        const bio_i18n = { ...d.bio_i18n };
        added.forEach((locale) => {
          if (!displayName_i18n[locale]) displayName_i18n[locale] = '';
          if (!bio_i18n[locale]) bio_i18n[locale] = '';
        });
        const removed = d.locales.filter((l) => !newLocales.includes(l));
        removed.forEach((locale) => {
          delete displayName_i18n[locale];
          delete bio_i18n[locale];
        });
        if (removed.includes(activeLocale)) {
          setActiveLocale(d.defaultLocale);
        }
        return { ...d, locales: newLocales, displayName_i18n, bio_i18n };
      });
    },
    [activeLocale]
  );

  const handleSubmitForReview = useCallback(async () => {
    setSubmitLoading(true);
    try {
      await manage.submit(slug);
      refetch();
    } finally {
      setSubmitLoading(false);
    }
  }, [slug, manage, refetch]);

  const handleArchive = useCallback(async () => {
    await manage.archive(slug);
    refetch();
    setArchiveConfirm(false);
  }, [slug, manage, refetch]);

  const classes = ['marketplace-publisher-manage', className].filter(Boolean).join(' ');

  // No permissions answer yet is not a denial: keep loading until the check settles.
  if ((loading && !publisher) || (publisher && permissionsLoading)) {
    return (
      <div className={classes}>
        <div className="marketplace-publisher-manage__loading">
          <span className="marketplace-marketplace__spinner" />
          <p>{t('marketplace.common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes}>
        <p className="marketplace-marketplace__error-message">{error}</p>
        <button type="button" className="marketplace-marketplace__retry-btn" onClick={refetch}>
          {t('marketplace.common.retry')}
        </button>
      </div>
    );
  }

  // Could not check: say so with Retry, never the "no permission" denial below.
  if (publisher && permissionsUnavailable) {
    return (
      <div className={classes}>
        <p className="marketplace-marketplace__error-message" data-testid="publisher-manage-permissions-unavailable">
          {t('marketplace.publishers.manage.permissionsUnavailable')}
        </p>
        <button type="button" className="marketplace-marketplace__retry-btn" onClick={refetchPermissions}>
          {t('marketplace.common.retry')}
        </button>
      </div>
    );
  }

  if (!publisher || !permissions?.canEdit) {
    return (
      <div className={classes}>
        <p>{t('marketplace.publishers.manage.noPermission')}</p>
      </div>
    );
  }

  const statusPillKey = STATUS_PILL_KEY[publisher.status];
  const currentDisplayName = draft?.displayName_i18n[activeLocale] ?? '';
  const currentBio = draft?.bio_i18n[activeLocale] ?? '';
  const isNonDefaultLocale = draft && activeLocale !== draft.defaultLocale;
  const hasChannels = channels.length > 0;

  const bannerStyle = {
    background: [
      `radial-gradient(ellipse at 80% 20%, hsla(${draft?.hue ?? publisher.hue}, 75%, 55%, 0.4) 0%, transparent 55%)`,
      `radial-gradient(ellipse at 15% 90%, hsla(${((draft?.hue ?? publisher.hue) + 50) % 360}, 65%, 45%, 0.35) 0%, transparent 50%)`,
      `linear-gradient(135deg, hsl(${draft?.hue ?? publisher.hue} 55% 18%) 0%, hsl(${draft?.hue ?? publisher.hue} 60% 12%) 50%, #062C58 100%)`,
    ].join(', '),
  } as React.CSSProperties;

  return (
    <div className={classes}>
      {/* Back button */}
      {onBack && (
        <button type="button" className="marketplace-publisher-manage__back" onClick={onBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.publishers.detail.back')}</span>
        </button>
      )}

      {/* Breadcrumb */}
      <nav
        className="marketplace-publisher-manage__breadcrumb"
        aria-label={t('marketplace.publishers.manage.title')}
      >
        <span className="marketplace-publisher-manage__breadcrumb-link">
          {t('marketplace.publishers.detail.breadcrumb.marketplace')}
        </span>
        <span className="marketplace-publisher-manage__breadcrumb-sep">{'›'}</span>
        <span className="marketplace-publisher-manage__breadcrumb-link">
          {t('marketplace.publishers.detail.breadcrumb.publishers')}
        </span>
        <span className="marketplace-publisher-manage__breadcrumb-sep">{'›'}</span>
        <span className="marketplace-publisher-manage__breadcrumb-link">
          {publisher.display_name}
        </span>
        <span className="marketplace-publisher-manage__breadcrumb-sep">{'›'}</span>
        <strong className="marketplace-publisher-manage__breadcrumb-current">
          {t('marketplace.publishers.manage.title')}
        </strong>
      </nav>

      {/* Identity bar */}
      <div className="marketplace-publisher-manage__identity-bar">
        <ScopeCoinAtom
          scope="metasphere"
          channelName={publisher.display_name}
          glyph={draft?.glyph ?? publisher.glyph}
          glyphKind={draft?.glyphKind ?? publisher.glyph_kind ?? 'letter'}
          hue={draft?.hue ?? publisher.hue}
          size="lg"
          isOfficial={publisher.is_official}
          className="marketplace-publisher-manage__identity-coin"
        />
        <div className="marketplace-publisher-manage__identity-info">
          <div className="marketplace-publisher-manage__identity-name-row">
            <h1 className="marketplace-publisher-manage__identity-name">
              {publisher.display_name}
            </h1>
            {statusPillKey && (
              <span
                className={`marketplace-publisher-manage__status-pill marketplace-publisher-manage__status-pill--${publisher.status}`}
              >
                <i className={`pi ${STATUS_PILL_ICON[publisher.status]}`} aria-hidden="true" />
                {t(statusPillKey)}
              </span>
            )}
          </div>
          <span className="marketplace-publisher-manage__identity-slug">
            /publishers/{publisher.slug}
          </span>
        </div>
        <button
          type="button"
          className="marketplace-publisher-manage__identity-view-btn"
        >
          <i className="pi pi-external-link" aria-hidden="true" />
          {t('marketplace.publishers.manage.identityBar.viewPublicPage')}
        </button>
      </div>

      {/* Two-column layout */}
      <div className="marketplace-publisher-manage__layout">
        {/* Nav rail */}
        <aside className="marketplace-publisher-manage__nav-rail">
          {publisher.status === 'pending_review' && (
            <div className="marketplace-publisher-manage__nav-callout marketplace-publisher-manage__nav-callout--warn">
              <div className="marketplace-publisher-manage__nav-callout-title">
                <i className="pi pi-clock" aria-hidden="true" />
                {t('marketplace.publishers.manage.navRail.pendingReviewTitle')}
              </div>
              <p className="marketplace-publisher-manage__nav-callout-desc">
                {t('marketplace.publishers.manage.navRail.pendingReviewDesc')}
              </p>
            </div>
          )}
          {publisher.status === 'rejected' && (
            <div className="marketplace-publisher-manage__nav-callout marketplace-publisher-manage__nav-callout--error">
              <div className="marketplace-publisher-manage__nav-callout-title">
                <i className="pi pi-times-circle" aria-hidden="true" />
                {t('marketplace.publishers.manage.navRail.rejectedTitle')}
              </div>
              <p className="marketplace-publisher-manage__nav-callout-desc">
                {t('marketplace.publishers.manage.navRail.rejectedDesc')}
              </p>
            </div>
          )}
          <nav
            className="marketplace-publisher-manage__nav"
            aria-label={t('marketplace.publishers.manage.navRail.ariaLabel')}
          >
            {NAV_SECTIONS.map(({ id, icon, danger }) => (
              <button
                key={id}
                type="button"
                className={[
                  'marketplace-publisher-manage__nav-item',
                  activeSection === id ? 'marketplace-publisher-manage__nav-item--active' : '',
                  danger ? 'marketplace-publisher-manage__nav-item--danger' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setActiveSection(id)}
              >
                <i
                  className={`pi ${icon} marketplace-publisher-manage__nav-icon`}
                  aria-hidden="true"
                />
                <span className="marketplace-publisher-manage__nav-label">
                  {t(`marketplace.publishers.manage.tabs.${id}`)}
                </span>
                {id === 'dangerZone' && (
                  <span className="marketplace-publisher-manage__nav-caution">
                    {t('marketplace.publishers.manage.dangerZone.caution')}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Section content */}
        <div className="marketplace-publisher-manage__sections">
          {saveError && (
            <p className="marketplace-marketplace__error-message">{saveError}</p>
          )}

          {/* PROFILE */}
          {activeSection === 'profile' && draft && (
            <div className="marketplace-publisher-manage__section-panels">
              <ManageSectionCard
                title={t('marketplace.publishers.manage.profile.publicProfileTitle')}
                subtitle={t('marketplace.publishers.manage.profile.publicProfileSubtitle')}
                action={
                  <LocaleManagerMolecule
                    locales={draft.locales}
                    defaultLocale={draft.defaultLocale}
                    onLocalesChange={handleLocalesChange}
                  />
                }
              >
                {draft.locales.length >= 1 && (
                  <LocaleTabsMolecule
                    locales={draft.locales}
                    defaultLocale={draft.defaultLocale}
                    activeLocale={activeLocale}
                    onLocaleChange={setActiveLocale}
                  />
                )}
                {isNonDefaultLocale && (
                  <div className="marketplace-publisher-manage__locale-hint">
                    <i className="pi pi-language" aria-hidden="true" />
                    <span>
                      {t('marketplace.publishers.manage.profile.localeTabsFallbackHint', {
                        locale: activeLocale,
                        defaultLocale: draft.defaultLocale,
                      })}
                    </span>
                  </div>
                )}

                <div className="marketplace-publisher-manage__field">
                  <label
                    className="marketplace-publisher-manage__label"
                    htmlFor="pub-display-name"
                  >
                    {t('marketplace.publishers.manage.profile.displayName')}
                  </label>
                  <input
                    id="pub-display-name"
                    type="text"
                    className="marketplace-publisher-manage__input"
                    value={currentDisplayName}
                    maxLength={80}
                    onChange={(e) =>
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              displayName_i18n: {
                                ...d.displayName_i18n,
                                [activeLocale]: e.target.value.slice(0, 80),
                              },
                            }
                          : d
                      )
                    }
                    placeholder={
                      isNonDefaultLocale
                        ? draft.displayName_i18n[draft.defaultLocale] || ''
                        : ''
                    }
                  />
                  <span className="marketplace-publisher-manage__hint">
                    {t('marketplace.publishers.manage.profile.displayNameHint')}
                  </span>
                </div>

                <div className="marketplace-publisher-manage__field">
                  <label className="marketplace-publisher-manage__label">
                    {t('marketplace.publishers.manage.profile.slugLabel')}
                  </label>
                  <div className="marketplace-publisher-manage__slug-row">
                    <span className="marketplace-publisher-manage__slug-prefix">publishers/</span>
                    <input
                      type="text"
                      className="marketplace-publisher-manage__input marketplace-publisher-manage__input--disabled"
                      value={publisher.slug}
                      disabled
                      readOnly
                    />
                  </div>
                  <span className="marketplace-publisher-manage__hint">
                    {t('marketplace.publishers.manage.profile.slugHint')}
                  </span>
                </div>

                <div className="marketplace-publisher-manage__field">
                  <label
                    className="marketplace-publisher-manage__label"
                    htmlFor="pub-bio"
                  >
                    {t('marketplace.publishers.manage.profile.bio')}
                  </label>
                  <textarea
                    id="pub-bio"
                    className="marketplace-publisher-manage__textarea"
                    rows={4}
                    value={currentBio}
                    maxLength={500}
                    onChange={(e) =>
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              bio_i18n: {
                                ...d.bio_i18n,
                                [activeLocale]: e.target.value.slice(0, 500),
                              },
                            }
                          : d
                      )
                    }
                    placeholder={
                      isNonDefaultLocale
                        ? draft.bio_i18n[draft.defaultLocale] ||
                          t('marketplace.publishers.manage.profile.bioFallbackPlaceholder')
                        : ''
                    }
                  />
                  <div className="marketplace-publisher-manage__bio-footer">
                    <span className="marketplace-publisher-manage__hint">
                      {t('marketplace.publishers.manage.profile.bioHint')}
                    </span>
                    <span className="marketplace-publisher-manage__char-count">
                      {currentBio.length} / 500
                    </span>
                  </div>
                </div>
              </ManageSectionCard>

              <ManageSectionCard
                title={t('marketplace.publishers.manage.profile.socialLinksTitle')}
                subtitle={t('marketplace.publishers.manage.profile.socialLinksSubtitle')}
              >
                <div className="marketplace-publisher-manage__social-grid">
                  <SocialInputRow
                    icon="pi-globe"
                    prefix="https://"
                    label={t('marketplace.publishers.manage.profile.websiteLabel')}
                    value={draft.website}
                    onChange={(v) => setDraft((d) => d ? { ...d, website: v } : d)}
                    placeholder="acme.example"
                  />
                  <SocialInputRow
                    icon="pi-linkedin"
                    prefix="linkedin.com/"
                    label={t('marketplace.publishers.manage.profile.linkedInLabel')}
                    value={draft.socials.linkedin ?? ''}
                    onChange={(v) =>
                      setDraft((d) =>
                        d ? { ...d, socials: { ...d.socials, linkedin: v || undefined } } : d
                      )
                    }
                    placeholder={t('marketplace.publishers.manage.profile.linkedInPlaceholder')}
                  />
                  <SocialInputRow
                    icon="pi-twitter"
                    prefix="x.com/"
                    label={t('marketplace.publishers.manage.profile.twitterLabel')}
                    value={draft.socials.twitter ?? ''}
                    onChange={(v) =>
                      setDraft((d) =>
                        d ? { ...d, socials: { ...d.socials, twitter: v || undefined } } : d
                      )
                    }
                    placeholder={t('marketplace.publishers.manage.profile.twitterPlaceholder')}
                  />
                  <SocialInputRow
                    icon="pi-github"
                    prefix="github.com/"
                    label={t('marketplace.publishers.manage.profile.githubLabel')}
                    value={draft.socials.github ?? ''}
                    onChange={(v) =>
                      setDraft((d) =>
                        d ? { ...d, socials: { ...d.socials, github: v || undefined } } : d
                      )
                    }
                    placeholder={t('marketplace.publishers.manage.profile.githubPlaceholder')}
                  />
                  <SocialInputRow
                    icon="pi-youtube"
                    prefix="youtube.com/"
                    label={t('marketplace.publishers.manage.profile.youtubeLabel')}
                    value={draft.socials.youtube ?? ''}
                    onChange={(v) =>
                      setDraft((d) =>
                        d ? { ...d, socials: { ...d.socials, youtube: v || undefined } } : d
                      )
                    }
                    placeholder={t('marketplace.publishers.manage.profile.youtubePlaceholder')}
                  />
                </div>
                <div className="marketplace-publisher-manage__social-preview">
                  <span className="marketplace-publisher-manage__social-preview-label">
                    {t('marketplace.publishers.manage.profile.socialLinksPreviewLabel')}
                  </span>
                  {draft.website || Object.values(draft.socials).some(Boolean) ? (
                    <SocialLinksMolecule
                      website={draft.website || null}
                      socials={draft.socials as PublisherSocials}
                    />
                  ) : (
                    <span className="marketplace-publisher-manage__social-preview-empty">
                      {t('marketplace.publishers.manage.profile.socialLinksPreviewEmpty')}
                    </span>
                  )}
                </div>
              </ManageSectionCard>
            </div>
          )}

          {/* BRANDING */}
          {activeSection === 'branding' && draft && (
            <div className="marketplace-publisher-manage__section-panels">
              <ManageSectionCard
                title={t('marketplace.publishers.manage.branding.logoTitle')}
                subtitle={t('marketplace.publishers.manage.branding.logoSubtitle')}
              >
                <div className="marketplace-publisher-manage__logo-row">
                  {/* Coin / logo preview — left side */}
                  {publisher.logoUrl ? (
                    <img
                      src={publisher.logoUrl}
                      alt={t('marketplace.publishers.manage.branding.logoPreviewAlt')}
                      className="marketplace-publisher-manage__logo-img"
                    />
                  ) : (
                    <ScopeCoinAtom
                      scope="metasphere"
                      channelName={publisher.display_name}
                      glyph={draft.glyph}
                      glyphKind={draft.glyphKind}
                      hue={draft.hue}
                      size="xl"
                      className="marketplace-publisher-manage__logo-preview"
                    />
                  )}

                  {/* Upload controls — right side */}
                  <div className="marketplace-publisher-manage__logo-controls">
                    {/* Three-button segmented control: Use letters / Choose an icon / Upload an image */}
                    <div className="marketplace-publisher-manage__logo-btn-row">
                      <button
                        type="button"
                        className={`marketplace-publisher-manage__btn-upload${draft.glyphKind === 'letter' && !publisher.logoUrl && !showLogoDropzone ? ' is-active' : ''}`}
                        onClick={() => {
                          setDraft((d) =>
                            d
                              ? { ...d, glyphKind: 'letter', glyph: publisher.display_name.charAt(0).toUpperCase() }
                              : d
                          );
                          setShowLogoDropzone(false);
                        }}
                      >
                        <i className="pi pi-font" aria-hidden="true" />
                        {t('marketplace.publishers.manage.branding.useInitial')}
                      </button>
                      <button
                        type="button"
                        className={`marketplace-publisher-manage__btn-ghost${draft.glyphKind === 'icon' && !publisher.logoUrl && !showLogoDropzone ? ' is-active' : ''}`}
                        onClick={() => {
                          setDraft((d) =>
                            d
                              ? { ...d, glyphKind: 'icon', glyph: d.glyph.includes(':') ? d.glyph : 'lucide:circle' }
                              : d
                          );
                          setShowLogoDropzone(false);
                        }}
                      >
                        <i className="pi pi-star" aria-hidden="true" />
                        {t('marketplace.publishers.manage.branding.chooseIcon')}
                      </button>
                      {publisher.logoUrl ? (
                        <button
                          type="button"
                          className="marketplace-publisher-manage__btn-ghost"
                          onClick={async () => {
                            if (!apiClients?.schemaClient) return;
                            await deletePublisherLogo(apiClients.schemaClient, publisher.slug);
                            refetch();
                          }}
                        >
                          <i className="pi pi-trash" aria-hidden="true" />
                          {t('marketplace.publishers.manage.branding.removeLogo')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`marketplace-publisher-manage__btn-ghost${showLogoDropzone ? ' is-active' : ''}`}
                          onClick={() => {
                            if (!showLogoDropzone) {
                              // Toggle ON: save current glyphKind and set to neutral
                              if (draft) {
                                previousGlyphKindRef.current = draft.glyphKind;
                                setDraft((d) =>
                                  d ? { ...d, glyphKind: 'letter' } : d
                                );
                              }
                              setShowLogoDropzone(true);
                            } else {
                              // Toggle OFF: restore previous glyphKind
                              setShowLogoDropzone(false);
                              setDraft((d) =>
                                d ? { ...d, glyphKind: previousGlyphKindRef.current } : d
                              );
                            }
                          }}
                        >
                          <i className="pi pi-image" aria-hidden="true" />
                          {t('marketplace.publishers.manage.branding.uploadLogo')}
                        </button>
                      )}
                    </div>

                    {/* Scanning status */}
                    {publisher.logo_attachment_id && !publisher.logoUrl && (
                      <StatusBadgeAtom
                        tone="info"
                        icon="pi pi-spin pi-spinner"
                        label={t('marketplace.publishers.manage.branding.scanning')}
                        size="small"
                      />
                    )}

                    {/* Expandable dropzone — shown when user clicks Upload logo */}
                    {showLogoDropzone && !publisher.logoUrl && (
                      <UploadAttachmentMolecule
                        onUpload={async (file) => {
                          if (!apiClients?.schemaClient) return;
                          await uploadPublisherLogo(apiClients.schemaClient, publisher.slug, file);
                          setShowLogoDropzone(false);
                          refetch();
                        }}
                        accept="image/*"
                        maxSizeBytes={2 * 1024 * 1024}
                        className="marketplace-publisher-manage__logo-dropzone"
                      />
                    )}

                    {/* INITIALS input — only shown when in letter mode and not uploading */}
                    {draft.glyphKind === 'letter' && !publisher.logoUrl && !showLogoDropzone && (
                      <div className="marketplace-publisher-manage__field">
                        <label className="marketplace-publisher-manage__label marketplace-publisher-manage__label--caps" htmlFor="pub-glyph">
                          {t('marketplace.publishers.manage.branding.glyph')}
                        </label>
                        <input
                          id="pub-glyph"
                          type="text"
                          maxLength={2}
                          className="marketplace-publisher-manage__input"
                          placeholder={t('marketplace.publishers.manage.branding.glyphPlaceholder')}
                          value={draft.glyph}
                          onChange={(e) =>
                            setDraft((d) => d ? { ...d, glyph: e.target.value } : d)
                          }
                        />
                        <p className="marketplace-publisher-manage__hint">
                          {t('marketplace.publishers.manage.branding.glyphHint')}
                        </p>
                      </div>
                    )}

                    {/* Icon picker — shown inline when in icon mode and not uploading */}
                    {draft.glyphKind === 'icon' && !publisher.logoUrl && !showLogoDropzone && (
                      <div className="marketplace-publisher-manage__field">
                        <IconPickerAtom
                          value={draft.glyph}
                          onChange={(icon) => setDraft((d) => (d ? { ...d, glyph: icon } : d))}
                          tooltip={t('marketplace.publishers.manage.branding.glyphIconHint')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </ManageSectionCard>

              <ManageSectionCard
                title={t('marketplace.publishers.manage.branding.bannerTitle')}
                subtitle={t('marketplace.publishers.manage.branding.bannerSubtitle')}
              >
                <div className="marketplace-publisher-manage__banner-preview" style={publisher.bannerUrl ? undefined : bannerStyle}>
                  {publisher.bannerUrl ? (
                    <img
                      src={publisher.bannerUrl}
                      alt={t('marketplace.publishers.manage.branding.bannerPreviewAlt')}
                      className="marketplace-publisher-manage__banner-img"
                    />
                  ) : (
                    <span className="marketplace-publisher-manage__banner-auto-label">
                      {t('marketplace.publishers.manage.branding.autoGeneratedLabel')}
                    </span>
                  )}
                </div>
                {/* Compact button pair — same pattern as logo section */}
                <div className="marketplace-publisher-manage__logo-btn-row">
                  {publisher.bannerUrl ? (
                    <button
                      type="button"
                      className="marketplace-publisher-manage__btn-ghost"
                      onClick={async () => {
                        if (!apiClients?.schemaClient) return;
                        await deletePublisherBanner(apiClients.schemaClient, publisher.slug);
                        refetch();
                      }}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                      {t('marketplace.publishers.manage.branding.removeBanner')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="marketplace-publisher-manage__btn-upload"
                      onClick={() => setShowBannerDropzone((v) => !v)}
                    >
                      <i className="pi pi-image" aria-hidden="true" />
                      {t('marketplace.publishers.manage.branding.uploadBanner')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="marketplace-publisher-manage__btn-ghost"
                    onClick={() => setShowBannerDropzone(false)}
                  >
                    <i className="pi pi-replay" aria-hidden="true" />
                    {t('marketplace.publishers.manage.branding.useAutoGenerated')}
                  </button>
                </div>

                {/* Hint text */}
                <p className="marketplace-publisher-manage__hint">
                  {t('marketplace.publishers.manage.branding.bannerHint')}
                </p>

                {/* Scanning status */}
                {publisher.banner_attachment_id && !publisher.bannerUrl && (
                  <StatusBadgeAtom
                    tone="info"
                    icon="pi pi-spin pi-spinner"
                    label={t('marketplace.publishers.manage.branding.scanning')}
                    size="small"
                  />
                )}

                {/* Expandable dropzone */}
                {showBannerDropzone && !publisher.bannerUrl && (
                  <UploadAttachmentMolecule
                    onUpload={async (file) => {
                      if (!apiClients?.schemaClient) return;
                      await uploadPublisherBanner(apiClients.schemaClient, publisher.slug, file);
                      setShowBannerDropzone(false);
                      refetch();
                    }}
                    accept="image/*"
                    maxSizeBytes={4 * 1024 * 1024}
                    className="marketplace-publisher-manage__logo-dropzone"
                  />
                )}
              </ManageSectionCard>

              <ManageSectionCard
                title={t('marketplace.publishers.manage.branding.accentTitle')}
                subtitle={t('marketplace.publishers.manage.branding.accentSubtitle')}
              >
                <div className="marketplace-publisher-manage__hue-presets">
                  {HUE_PRESETS.map((hue) => (
                    <button
                      key={hue}
                      type="button"
                      className={[
                        'marketplace-publisher-manage__hue-preset',
                        draft.hue === hue ? 'marketplace-publisher-manage__hue-preset--active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{
                        background: `linear-gradient(155deg, hsl(${hue} 65% 60%) 0%, hsl(${(hue + 30) % 360} 58% 40%) 100%)`,
                      }}
                      onClick={() => setDraft((d) => d ? { ...d, hue } : d)}
                      aria-pressed={draft.hue === hue}
                      aria-label={`${hue}°`}
                    >
                      {draft.hue === hue && (
                        <i className="pi pi-check marketplace-publisher-manage__hue-preset-check" aria-hidden="true" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="marketplace-publisher-manage__hue-slider-row">
                  <span className="marketplace-publisher-manage__hue-slider-label">
                    {t('marketplace.publishers.manage.branding.customHueLabel')}
                  </span>
                  <input
                    type="range"
                    className="marketplace-publisher-manage__hue-slider"
                    min={0}
                    max={359}
                    value={draft.hue}
                    onChange={(e) =>
                      setDraft((d) => d ? { ...d, hue: Number.parseInt(e.target.value, 10) } : d)
                    }
                    aria-label={t('marketplace.publishers.manage.branding.customHueLabel')}
                  />
                  <span className="marketplace-publisher-manage__hue-value">{draft.hue}°</span>
                </div>
              </ManageSectionCard>
            </div>
          )}

          {/* CHANNELS */}
          {activeSection === 'channels' && (
            <div className="marketplace-publisher-manage__section-panels">
              <ManageSectionCard
                title={t('marketplace.publishers.manage.channels.channelsTitle')}
                subtitle={t('marketplace.publishers.manage.channels.channelsSubtitle')}
                action={
                  onCreateChannel ? (
                    <button
                      type="button"
                      className="marketplace-publisher-manage__btn-primary"
                      onClick={onCreateChannel}
                    >
                      <i className="pi pi-plus" aria-hidden="true" />
                      {t('marketplace.publishers.manage.channels.newChannel')}
                    </button>
                  ) : undefined
                }
              >
                {!hasChannels ? (
                  <p className="marketplace-publisher-manage__empty">
                    {t('marketplace.publishers.manage.channels.empty')}
                  </p>
                ) : (
                  <div className="marketplace-publisher-manage__channel-list-v2">
                    {channels.map((channel) => (
                      <ManagedChannelRow
                        key={channel.id}
                        channel={channel}
                        publisherHue={publisher.hue}
                        onOpen={() => onChannelClick?.(channel.id)}
                      />
                    ))}
                  </div>
                )}
              </ManageSectionCard>
            </div>
          )}

          {/* TEAM — always mounted so usePublisherMembers fetches on page load,
               not on first tab click. Hidden via CSS when another section is active. */}
          {permissions && (
            <div className={[
              'marketplace-publisher-manage__section-panels',
              activeSection === 'team' ? '' : 'marketplace-publisher-manage__section-panels--hidden',
            ].filter(Boolean).join(' ')}>
              <PublisherTeamSectionOrganism
                publisherId={publisher.id}
                permissions={permissions}
                onSelfRemoved={() => {
                  // Caller lost their role — navigate back so they don't hit a 403 on refetch
                  onBack?.();
                }}
              />
            </div>
          )}

          {/* VERIFICATION */}
          {activeSection === 'verification' && (
            <div className="marketplace-publisher-manage__section-panels">
              <ManageSectionCard
                title={t('marketplace.publishers.manage.verification.marketplaceStatusTitle')}
                subtitle={t('marketplace.publishers.manage.verification.marketplaceStatusSubtitle')}
              >
                <PublisherStatusCalloutMolecule
                  status={publisher.status}
                  reviewerNote={publisher.reviewer_note}
                  reviewedAt={publisher.reviewed_at ? String(publisher.reviewed_at) : null}
                />
                {publisher.status === 'draft' && (
                  <div className="marketplace-publisher-manage__submit-section">
                    <p className="marketplace-publisher-manage__submit-hint">
                      {t('marketplace.publishers.manage.verification.submitHint')}
                    </p>
                    <button
                      type="button"
                      className="marketplace-publisher-manage__btn-primary"
                      onClick={handleSubmitForReview}
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <>
                          <span className="marketplace-marketplace__spinner" />
                          {t('marketplace.publishers.manage.verification.submitting')}
                        </>
                      ) : (
                        t('marketplace.publishers.manage.verification.submit')
                      )}
                    </button>
                  </div>
                )}
              </ManageSectionCard>

              <ManageSectionCard
                title={t('marketplace.publishers.manage.verification.activityTitle')}
                subtitle={t('marketplace.publishers.manage.verification.activityLogSubtitle')}
              >
                {activityLoading ? (
                  <span className="marketplace-marketplace__spinner" />
                ) : activity.length === 0 ? (
                  <p className="marketplace-publisher-manage__empty">
                    {t('marketplace.publishers.manage.verification.noActivity')}
                  </p>
                ) : (
                  <div className="marketplace-publisher-manage__activity-list">
                    {activity.map((row) => (
                      <MarketplaceActivityRowMolecule key={row.id} activity={row} />
                    ))}
                  </div>
                )}
              </ManageSectionCard>

              {publisher.is_official ? (
                <ManageSectionCard
                  title={t('marketplace.publishers.manage.verification.officialBadgeTitle')}
                  subtitle={t('marketplace.publishers.manage.verification.officialDesc')}
                >
                  <div className="marketplace-publisher-manage__official-row">
                    <span
                      className="marketplace-publisher-manage__official-icon-wrap"
                      aria-hidden="true"
                    >
                      <i className="pi pi-verified" />
                    </span>
                    <div className="marketplace-publisher-manage__official-info">
                      <span className="marketplace-publisher-manage__official-name">
                        {t('marketplace.publishers.manage.verification.officialPublisherLabel')}
                      </span>
                      <span className="marketplace-publisher-manage__official-desc">
                        {t('marketplace.publishers.manage.verification.officialGranted')}
                      </span>
                    </div>
                  </div>
                </ManageSectionCard>
              ) : publisher.status === 'published' ? (
                <ManageSectionCard
                  title={t('marketplace.publishers.manage.verification.applyOfficialTitle')}
                  subtitle={t('marketplace.publishers.manage.verification.applyOfficialSubtitle')}
                >
                  <button
                    type="button"
                    className="marketplace-publisher-manage__btn-primary"
                    disabled
                    title={t('marketplace.publishers.manage.verification.applyOfficialComingSoon')}
                  >
                    <i className="pi pi-verified" aria-hidden="true" />
                    {t('marketplace.publishers.manage.verification.applyOfficialBtn')}
                  </button>
                </ManageSectionCard>
              ) : null}
            </div>
          )}

          {/* DANGER ZONE */}
          {activeSection === 'dangerZone' && (
            <div className="marketplace-publisher-manage__section-panels">
              <ManageSectionCard
                title={t('marketplace.publishers.manage.dangerZone.transferTitle')}
                subtitle={t('marketplace.publishers.manage.dangerZone.transferDesc')}
                danger
              >
                <button
                  type="button"
                  className="marketplace-publisher-manage__btn-secondary"
                  disabled
                  title={t('marketplace.publishers.manage.dangerZone.transferComingSoon')}
                >
                  <i className="pi pi-arrow-right-arrow-left" aria-hidden="true" />
                  {t('marketplace.publishers.manage.dangerZone.transferBtn')}
                </button>
              </ManageSectionCard>

              <ManageSectionCard
                title={t('marketplace.publishers.manage.dangerZone.archiveTitle')}
                subtitle={t('marketplace.publishers.manage.dangerZone.archiveDesc')}
                danger
              >
                {!archiveConfirm ? (
                  <button
                    type="button"
                    className="marketplace-publisher-manage__btn-warning"
                    onClick={() => setArchiveConfirm(true)}
                  >
                    <i className="pi pi-inbox" aria-hidden="true" />
                    {t('marketplace.publishers.manage.dangerZone.archiveBtn')}
                  </button>
                ) : (
                  <div className="marketplace-publisher-manage__confirm-row">
                    <p className="marketplace-publisher-manage__confirm-text">
                      {t('marketplace.publishers.manage.dangerZone.archiveConfirm')}
                    </p>
                    <div className="marketplace-publisher-manage__confirm-actions">
                      <button
                        type="button"
                        className="marketplace-publisher-manage__danger-btn"
                        onClick={handleArchive}
                        disabled={manage.archiving}
                      >
                        {t('marketplace.publishers.manage.dangerZone.archiveConfirmBtn')}
                      </button>
                      <button
                        type="button"
                        className="marketplace-publisher-manage__cancel-btn"
                        onClick={() => setArchiveConfirm(false)}
                      >
                        {t('marketplace.publishers.manage.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </ManageSectionCard>

              <ManageSectionCard
                title={t('marketplace.publishers.manage.dangerZone.deleteTitle')}
                subtitle={t('marketplace.publishers.manage.dangerZone.deleteDesc')}
                danger
              >
                {!deleteConfirm ? (
                  <>
                    <button
                      type="button"
                      className="marketplace-publisher-manage__danger-btn"
                      disabled={hasChannels}
                      onClick={() => setDeleteConfirm(true)}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                      {t('marketplace.publishers.manage.dangerZone.deleteBtn')}
                    </button>
                    {hasChannels && (
                      <p className="marketplace-publisher-manage__hint">
                        {t('marketplace.publishers.manage.dangerZone.deleteBlockedHint')}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="marketplace-publisher-manage__confirm-row">
                    <p className="marketplace-publisher-manage__confirm-text">
                      {t('marketplace.publishers.manage.dangerZone.deleteConfirm')}
                    </p>
                    <div className="marketplace-publisher-manage__confirm-actions">
                      {/* TODO(EAINT-619): wire to manage.delete when API is implemented */}
                      <button
                        type="button"
                        className="marketplace-publisher-manage__danger-btn"
                        disabled
                        title={t('marketplace.publishers.manage.dangerZone.deleteComingSoon')}
                      >
                        {t('marketplace.publishers.manage.dangerZone.deleteConfirmBtn')}
                      </button>
                      <button
                        type="button"
                        className="marketplace-publisher-manage__cancel-btn"
                        onClick={() => setDeleteConfirm(false)}
                      >
                        {t('marketplace.publishers.manage.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </ManageSectionCard>
            </div>
          )}
        </div>
      </div>

      <StickyUnsavedBarMolecule
        isDirty={isDirty}
        saving={manage.saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </div>
  );
};

PublisherManageOrganism.displayName = 'PublisherManageOrganism';

export default PublisherManageOrganism;
